import React from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useHistory } from 'react-router-dom';
import { getData } from '../../../../utils/functional/fp';
import { useAPIErrorHandler } from '../../../../components/hooks/useAPIErrorHandler';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { useDocumentDownload } from '../../../../utils/documents.utils';
import { downloadDocument } from '../../../../api/DocumentsService';
import { useAccountDetails } from '../../../../components/hooks/queries/accounts/useAccountDetails';
import {
  generateRiskReport,
  getRiskReportDocInfo,
} from '../../../../api/reports/reports.api';
import { isPromise } from '../../../../utils/app.utils';
import { usePersona } from '../../../../components/hooks/usePersona';
import { sleep } from '../../../../utils/appUtils';
import { useLinkToAccountProfile } from '../../../accounts/admin/profile/CowbellAccountProfileLink';
import { useGetAccountProfileLink } from '../../../../components/hooks/useGetAccountProfileLink';
import { riskReportDocInfoQueryKey } from './riskReportQueryKeys';

type UseRiskReportDocInfoQueryProps = { accountId: string };
export const useRiskReportDocInfoQuery = (
  props: UseRiskReportDocInfoQueryProps
) => {
  return useQuery({
    queryKey: riskReportDocInfoQueryKey(props.accountId),
    queryFn: () =>
      getRiskReportDocInfo({ params: { accountId: props.accountId } }).then(
        getData
      ),
    retry: 1,
  });
};

type UseRiskReportDoc = { accountId: string };
export const useRiskReportDoc = (props: UseRiskReportDoc) => {
  const handleAPIError = useAPIErrorHandler();
  const notifications = useNotifications();
  const history = useHistory();
  const documentDownload = useDocumentDownload();

  const linkToProfile = useLinkToAccountProfile({
    account: { id: props.accountId },
  });
  const cowbellAccountProfileLink = useGetAccountProfileLink(props.accountId);

  const queryClient = useQueryClient();

  const persona = usePersona();

  const riskReportDocInfoQuery = useRiskReportDocInfoQuery({
    accountId: props.accountId,
  });

  const accountQuery = useAccountDetails(props.accountId);
  const accountHasDomain =
    accountQuery.data == null ? true : Boolean(accountQuery.data.domainName);

  const addDomainUrl = persona.isInsured
    ? `/customer/manage/profile/${props.accountId}`
    : cowbellAccountProfileLink;

  const downloadReport = () => {
    const accountName = accountQuery.data?.name ?? '';
    return documentDownload({
      asyncDownload: () =>
        downloadDocument({ docId: riskReportDocInfoQuery.data?.docId }),
      blobFileParams: {
        docName: `CowbellCyber_RiskReport-${accountName}`,
      },
    });
  };

  const generateReport = () => {
    if (!accountHasDomain) {
      notifications.show({
        type: 'warning',
        title: 'Missing domain name',
        body: (
          <>
            Account is missing a valid domain name.
            <br />
            Please update it before generating a Risk Report.
          </>
        ),
      });
      return;
    }

    const invalidateQueries = () => {
      queryClient.invalidateQueries(riskReportDocInfoQueryKey(props.accountId));
    };

    return generateRiskReport({ params: { accountId: props.accountId } })
      .then(() => {
        notifications.show({
          type: 'success',
          title: 'Risk Report Generated',
          body: 'The document should be available soon.',
        });
      })
      .then(() => sleep(2000))
      .then(invalidateQueries)
      .catch(handleAPIError('Unable to generate Risk Report at this time'));
  };

  const redirectToAddDomain = () => {
    linkToProfile();
    history.push(addDomainUrl);
  };

  const latestReportDate =
    riskReportDocInfoQuery.data?.metadata?.generatedDate ?? null;

  const isRiskScannerRunning = Boolean(
    riskReportDocInfoQuery.data?.isScannerRunning
  );

  const isReportGenerating = Boolean(
    riskReportDocInfoQuery.data?.isReportGenerating
  );

  const isGenerationDisabled = isRiskScannerRunning || isReportGenerating;

  const isDownloadDisabled = !riskReportDocInfoQuery.data || !latestReportDate;

  return {
    methods: {
      downloadReport,
      generateReport,
      redirectToAddDomain,
    },
    data: {
      latestReportDate,
      accountHasDomain,
    },
    isLoading: riskReportDocInfoQuery.isLoading || accountQuery.isLoading,
    isRiskScannerRunning,
    isReportGenerating,
    isDownloadDisabled,
    isGenerationDisabled,
  };
};

type UseAsyncEffectWithLoading = { effect: () => Promise<unknown> | void };
export const useAsyncEffectWithLoading = (props: UseAsyncEffectWithLoading) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const request = () => {
    setIsLoading(true);

    const req = props.effect();
    if (isPromise(req)) {
      req.finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    request,
  };
};
